/**
 * useCustomerTranslation Hook
 *
 * Custom hook for customer module translations
 * Provides type-safe access to customer namespace translations
 *
 * Usage:
 * ```tsx
 * import { useCustomerTranslation } from '@/hooks/useCustomerTranslation';
 *
 * const MyComponent = () => {
 *   const { t } = useCustomerTranslation();
 *   return <h1>{t('customer.list.title')}</h1>;
 * };
 * ```
 */

import { useTranslation } from 'react-i18next';

/**
 * Hook for accessing customer module translations
 *
 * @returns Translation functions and utilities scoped to customers namespace
 */
export const useCustomerTranslation = () => {
  // Use 'customers' namespace to access customer-specific translations
  const { t, i18n } = useTranslation(['customers', 'common']);

  return {
    /**
     * Translation function for customer module
     * @param key - Translation key (e.g., 'customer.list.title')
     * @param options - Optional interpolation values
     */
    t,

    /**
     * i18next instance for advanced usage
     */
    i18n,

    /**
     * Current language code
     */
    language: i18n.language,

    /**
     * Change language
     * @param lang - Language code ('en', 'zh')
     */
    changeLanguage: (lang: string) => i18n.changeLanguage(lang),
  };
};

/**
 * Type-safe customer translation keys (for IDE autocomplete)
 * Add more specific types as needed
 */
export type CustomerTranslationKey =
  | 'customer.list.title'
  | 'customer.list.create'
  | 'customer.fields.customerId'
  | 'customer.fields.customerName'
  | 'customer.actions.edit'
  | 'customer.actions.delete'
  | 'customer.status.active'
  | 'customer.status.inactive'
  | string; // Allow any string for flexibility

/**
 * Default export
 */
export default useCustomerTranslation;
